const GRID_LINE_ENDPOINT_FLOATS = 4;

/**
 * Device-px position of a line, so it covers whole pixels: a line of odd
 * width is centred on a pixel centre, of even width on a pixel edge.
 *
 * @param {number} cssPosition
 * @param {number} pixelRatio
 * @param {number} lineWidth - device px
 * @returns {number}
 */
export const snapLine = (cssPosition, pixelRatio, lineWidth) =>
  Math.floor(cssPosition * pixelRatio) + (lineWidth % 2) / 2;

/**
 * Device-px x of a vertical line at a bar position: snapped as snapLine, or
 * exact (centred on the line width) while the view follows time, so the line
 * moves with the bars in sub-pixels.
 *
 * @param {number} cssPosition
 * @param {number} pixelRatio
 * @param {number} lineWidth - device px
 * @param {boolean} isSnappedX
 * @returns {number}
 */
export const timeLineX = (cssPosition, pixelRatio, lineWidth, isSnappedX) =>
  isSnappedX ? snapLine(cssPosition, pixelRatio, lineWidth) : cssPosition * pixelRatio;

/**
 * Line segments of the grid in device pixels: a vertical line at every time
 * label and a horizontal one at every price label. Rebuilt each frame — it is
 * a few dozen segments.
 *
 * @param {object} params
 * @param {number[]} params.timeXs   - x of time labels, CSS px
 * @param {number[]} params.priceYs  - y of price labels, CSS px
 * @param {{ width: number, height: number }} params.pane - CSS px
 * @param {number} params.pixelRatio
 * @param {number[]} params.color    - RGBA 0..1
 * @param {number} params.floatsPerLine - lineRenderer.floatsPerInstance
 * @param {boolean} params.isSnappedX - false keeps the time lines sub-pixel (while the view follows time)
 * @returns {Float32Array}
 */
export const buildGridLines = ({ timeXs, priceYs, pane, pixelRatio, color, floatsPerLine, isSnappedX }) => {
  const lineWidth = Math.max(1, Math.floor(pixelRatio));
  const paneWidth = pane.width * pixelRatio;
  const paneHeight = pane.height * pixelRatio;
  const data = new Float32Array((timeXs.length + priceYs.length) * floatsPerLine);
  let offset = 0;

  const addLine = (endpoints) => {
    data.set(endpoints, offset);
    data.set(color, offset + GRID_LINE_ENDPOINT_FLOATS);
    data[offset + floatsPerLine - 1] = lineWidth;
    offset += floatsPerLine;
  };

  timeXs.forEach((x) => {
    const deviceX = timeLineX(x, pixelRatio, lineWidth, isSnappedX);

    addLine([deviceX, 0, deviceX, paneHeight]);
  });
  priceYs.forEach((y) => {
    const deviceY = snapLine(y, pixelRatio, lineWidth);

    addLine([0, deviceY, paneWidth, deviceY]);
  });

  return data;
};
